// CapCaptcha.tsx
import * as React from 'react';

// Add custom element type for 'cap-widget'
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'cap-widget': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        'data-cap-api-endpoint'?: string;
        'data-cap-hidden-field-name'?: string;
        className?: string;
        ref?: React.Ref<HTMLElement>;
      };
    }
  }
}

type CapCaptchaProps = {
  className?: string;
  onSolve?: (token: string) => void;
  onError?: (error: unknown) => void;
};

export function CapCaptcha({ className, onSolve, onError }: CapCaptchaProps) {
  const widgetRef = React.useRef<HTMLElement | null>(null);
  const [ready, setReady] = React.useState(false);

  // Load the Cap widget client-side only
  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      if (typeof window === 'undefined') return;
      try {
        await import('@cap.js/widget'); // registers <cap-widget>
        if (!cancelled) setReady(true);
      } catch (e) {
        console.error('Failed to load @cap.js/widget', e);
        onError?.(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [onError]);

  // Wire up solve / error events
  React.useEffect(() => {
    if (!ready || !widgetRef.current) return;

    const el = widgetRef.current as any;

    const handleSolve = (event: CustomEvent) => {
      const token = event.detail?.token;
      onSolve?.(token);
    };

    const handleError = (event: CustomEvent) => {
      console.error('[cap] error', event.detail);
      onError?.(event.detail ?? event);
    };

    el.addEventListener('solve', handleSolve as EventListener);
    el.addEventListener('error', handleError as EventListener);

    return () => {
      el.removeEventListener('solve', handleSolve as EventListener);
      el.removeEventListener('error', handleError as EventListener);
    };
  }, [ready, onSolve, onError]);

  if (!ready) return null;

  return (
    <div className={`captcha-wrapper ${className ?? ''}`}>
      <cap-widget
        ref={widgetRef as any}
        data-cap-api-endpoint={process.env.GATSBY_CAPTCHA_CHALLENGE}
        data-cap-hidden-field-name='PowToken'
      />
    </div>
  );
}
